import { useEffect, useId, useState, type KeyboardEvent } from 'react';
import { Search, X } from 'lucide-react';
import { Icon } from '@/shared/components/Icon';
import { Input } from '@/shared/components/Input';
import { cn } from '@/shared/helpers/cn';
import styles from './SearchInput.module.scss';

export interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** Rotulo do campo. Nomeia a busca para leitor de tela e, com `showLabel`, na tela. */
  label?: string;
  /**
   * Mostra o `label` acima do campo.
   *
   * Padrao do sistema para **filtro de listagem**: um campo de busca solto numa
   * barra nao diz o que filtra nem em que campos procura, e o texto de exemplo
   * some no instante em que alguem digita. Dentro de formulario, onde o
   * `FormField` ja rotula, mantenha desligado.
   */
  showLabel?: boolean;
  /** Atraso do debounce em ms. */
  debounceMs?: number;
  className?: string;
}

/**
 * Campo de busca com debounce.
 *
 * O estado digitado e local e so sobe ao consumidor apos o atraso — isso evita
 * uma requisicao por tecla. `Enter` submete imediatamente, sem esperar.
 *
 * **Nao e um `<form>`.** O componente aparece dentro de formularios — o filtro
 * de procedimentos do modal de agendamento e um deles — e formulario aninhado e
 * HTML invalido: o navegador descarta o interno, e o `Enter` no campo de busca
 * passa a submeter o formulario de fora. Num modal de agendamento isso significa
 * salvar o agendamento ao tentar filtrar. O papel `search` vem do atributo, e o
 * `Enter` e tratado no proprio campo.
 */
export function SearchInput({
  value,
  onChange,
  placeholder = 'Pesquisar...',
  label = 'Pesquisar',
  showLabel = false,
  debounceMs = 300,
  className,
}: SearchInputProps) {
  const id = useId();
  const [draft, setDraft] = useState(value);

  // Mantem o rascunho em sincronia quando o valor e limpo de fora (ex.: filtro).
  useEffect(() => {
    setDraft(value);
  }, [value]);

  useEffect(() => {
    if (draft === value) return;
    const timer = setTimeout(() => onChange(draft), debounceMs);
    return () => clearTimeout(timer);
  }, [draft, debounceMs, onChange, value]);

  const aoTeclar = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key !== 'Enter') return;
    // Impede que o `Enter` chegue a um formulario que envolva este campo.
    event.preventDefault();
    onChange(draft);
  };

  return (
    <div className={cn(styles.root, className)} role="search">
      {showLabel ? (
        <label className={styles.label} htmlFor={id}>
          {label}
        </label>
      ) : null}

      <Input
        id={id}
        type="search"
        aria-label={showLabel ? undefined : label}
        placeholder={placeholder}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={aoTeclar}
        prefix={<Icon as={Search} size={16} />}
        suffix={
          draft ? (
            <button
              type="button"
              className={styles.clear}
              onClick={() => {
                setDraft('');
                onChange('');
              }}
              aria-label="Limpar busca"
            >
              <Icon as={X} size={16} />
            </button>
          ) : null
        }
      />
    </div>
  );
}
